import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Landmark, MessageSquare, TrendingUp, Users
} from 'lucide-react';

const InvestorsPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1e3a5f]/10 text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Landmark className="w-4 h-4" />
            <span>For Investors</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] leading-tight mb-6">
            Partner With Us
          </h1>
          
          <p className="text-lg text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            We're open to capital partnership conversations with investors interested in IP asset development.
          </p>
          
          <Link to="/contact">
            <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-xl shadow-[#1e3a5f]/20 transition-all duration-300 hover:-translate-y-0.5">
              Start a Conversation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-8 md:p-12">
              <p className="text-lg text-slate-600 leading-relaxed">
                IpFunders funds IP protection—primarily patents—for technology companies and inventors. We shift upfront cost and uncertainty to ourselves, aligning investment with progress over time. If you're interested in the IP asset space, we'd like to hear from you.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              What Makes This Different
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">Risk Isolation</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Prosecution uncertainty is absorbed upfront. Investment aligns with demonstrated progress, not speculative outcomes.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">Clean Structures</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Predictable economics. No complex contingencies. Designed to be compatible with standard venture financing norms.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We're Building */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Card className="bg-white border-slate-200 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#0f1f2e] mb-4">
                  What We're Building
                </h2>
              </div>
              
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  IpFunders funds IP protection for technology companies and independent inventors. Patents represent the first and most standardized asset class within IP protection funding. We shift the upfront cost and uncertainty to ourselves, allowing counterparties to pursue protection without large early commitments.
                </p>
                <p>
                  Investment aligns with progress. Commitment grows only as the IP matures and demonstrates value. Counterparties retain control and flexibility throughout.
                </p>
                <p>
                  We're in the early stages of building this model and welcome conversations with investors who see opportunity in the IP asset space.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0f1f2e]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Users className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interested in Learning More?
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            We'd welcome the opportunity to discuss how we might work together.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#0f1f2e] hover:bg-slate-100 px-10 py-6 text-base font-semibold shadow-xl transition-all duration-300 hover:-translate-y-0.5">
              <MessageSquare className="mr-2 w-5 h-5" />
              Start a Conversation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InvestorsPage;
